import { ILandingPagesBuilderSchema } from '@playbook-technologies/common-schemas';
import { defaultFeatures } from './OTPFeaturesSection';
import { toImgSrcRaw, toVideoSrcRaw } from './helpers';
import { defaultReviews, defaultFaqs } from './helpers';
import {
  defaultSubscriptionPageConfig,
  inheritedSectionFromCreatorLanding,
} from '@playbook-technologies/common-schemas/lib/schemas/defaultSubCustomProgramPage';
import { extendSectionsType } from './mapper';
import { OfferDetailsData } from './OfferDetails.mappers';
import {
  OfferDetailsSectionSchema,
  IntroductionSectionSchema,
} from './OTPLandingPage.schema';
import { ISection } from './schemas';
import { GetProgramPurchasePageByIdQuery } from '../../../generated/graphql';

export function defaultOTPPageConfig(
  program: GetProgramPurchasePageByIdQuery['program'] | null | undefined,
  {
    primaryColor,
    colors,
    landingPageConfig,
  }: {
    primaryColor: string;
    colors: string[];
    landingPageConfig: ILandingPagesBuilderSchema | null;
  },
) {
  const inheritedSections = inheritedSectionFromCreatorLanding(
    landingPageConfig,
    {
      primaryColor,
    },
  );

  return {
    sections: [
      {
        enabled: true,
        type: 'Introduction',
        title: program?.name || 'Program Title',
        subtitle: 'Program Subtitle',
        desc: program?.description || 'Program Description',
        coverImage: toImgSrcRaw(program?.mediaCover) || '',
        introVideo: toVideoSrcRaw(program?.mediaIntro),
        primaryColor,
        logo: landingPageConfig?.branding?.logo || '',
        colorIndex: 1,
        bgColor: colors[1],
        priceBefore: null,
        cta: {},
      },
      {
        type: 'EmailCapture',
        enabled: false,
        bgColor: colors[2],
        colorIndex: 2,
        title: '',
        description: '',
        displayAsPopup: true,
      },
      {
        enabled: true,
        colorIndex: 0,
        bgColor: colors[0],
        type: 'EmailCapture',
        title: '',
        description: '',
        image: null,
      },
      {
        enabled: true,
        type: 'ProgramStats',
        bgColor: colors[2],
        colorIndex: 2,
      },
      {
        enabled: true,
        type: 'TextSection1',
        title: 'About the program',
        desc: program?.description || 'Program description',
        coverImage: toImgSrcRaw(program?.mediaCover) || '',
        introVideo: toVideoSrcRaw(program?.mediaIntro),
        colorIndex: 2,
        bgColor: colors[2],
        cta: {},
      },
      {
        enabled: true,
        type: 'Workouts',
        title: 'Workouts',
        bgColor: colors[2],
        colorIndex: 2,
        workoutIds: [],
      },
      inheritedSections['Reviews'] ?? {
        enabled: true,
        type: 'Reviews',
        title: 'Glowing 5-Star Reviews from Satisfied Customers',
        reviews: [...defaultReviews],
        bgColor: colors[1],
        colorIndex: 1,
      },
      inheritedSections['Testimonials'] ?? {
        enabled: true,
        type: 'Testimonials',
        title: 'Need to see it to believe?',
        testimonials: program ? parseProgramTestimonials(program) : [],
        bgColor: colors[0],
        colorIndex: 0,
      },
      {
        enabled: true,
        type: 'OTPFeatures',
        title: 'Program Features',
        bgColor: colors[1],
        colorIndex: 1,
        features: defaultFeatures,
      },
      inheritedSections['Bio'] ?? {
        enabled: true,
        type: 'Bio',
        title: program?.influencer?.bio || 'Online personal trainer',
        desc: program?.influencer?.messageProfile || '',
        coverImage: toImgSrcRaw(program?.influencer?.mediaAvatar),
        bgColor: colors[0],
        colorIndex: 0,
        introVideo: null,
      },
      {
        enabled: true,
        type: 'TextSection2',
        title: 'Get support & accountability from our amazing community',
        desc:
          program?.influencer?.messageCommunity ||
          `Find encouragement and accountability by connecting with others in our exclusive private community. There you can learn about nutrition, learn more about form, and connect with other members.${
            program?.influencer?.firstName
              ? ` Bonus: ${program.influencer.firstName} will be right there to cheer on you.`
              : ''
          }`,
        images: [{ url: null }, { url: null }],
        bgColor: colors[0],
        colorIndex: 0,
        cta: {},
      },
      {
        enabled: true,
        type: 'TextSection2',
        title: 'Bonus material',
        desc: 'Additional bonus content',
        bullets: [],
        images: [{ url: null }, { url: null }],
        rotateImages: true,
        bgColor: colors[1],
        colorIndex: 1,
        cta: {},
      },
      inheritedSections['FAQs'] ?? {
        enabled: true,
        type: 'FAQs',
        title: 'Frequently asked questions',
        faqs: [...(program?.faq?.length ? program.faq : defaultFaqs)],
        bgColor: colors[2],
        colorIndex: 2,
      },
    ],
  };
}

export function loadOfferPageConfig(
  program: GetProgramPurchasePageByIdQuery['program'] | null | undefined,
  serverConfig: { sections?: ISection[] } | null,
  landingPageConfig: ILandingPagesBuilderSchema | null,
  { primaryColor, colors }: { primaryColor: string; colors: string[] },
  offerData?: OfferDetailsData,
  offerMeta?: { title?: string | null; description?: string | null },
): { sections: ISection[] } {
  // If serverConfig exists, use it (existing saved config)
  if (serverConfig?.sections) {
    return {
      sections: extendSectionsType(serverConfig.sections),
      ...serverConfig,
    };
  }

  // Create Introduction section for offer mode
  // Use creator/landing page data as fallback since program is not available
  const offerName = offerMeta?.title?.trim() ? offerMeta.title.trim() : null;

  const description =
    typeof offerMeta?.description === 'string'
      ? offerMeta.description.trim()
      : null;

  const introSection: IntroductionSectionSchema = {
    enabled: true,
    type: 'Introduction' as const,
    title: offerName || 'Offer Title',
    subtitle: 'Welcome to my offer',
    desc: description && description.length > 0 ? description : '',
    coverImage: '',
    introVideo: null,
    primaryColor,
    logo: landingPageConfig?.branding?.logo || '',
    colorIndex: 1,
    bgColor: colors[1],
    priceBefore: null,
    cta: {},
  };

  // Create typed OfferDetails section with defaults
  const offerDetailsSection: OfferDetailsSectionSchema = {
    enabled: true,
    type: 'OfferDetails',
    headlineText: offerName ?? "What's included?",
    programIds: offerData?.programIds.options.map((opt) => opt.value) || [],
    extrasIds: offerData?.extrasIds.options.map((opt) => opt.value) || [],
    communityFeatures:
      offerData?.communityFeatures.options.map((opt) => opt.value) || [],
    bgColor: colors[2],
    colorIndex: 2,
  };

  // Default config: only Introduction + OfferDetails
  const sections: ISection[] = [
    introSection as unknown as ISection,
    offerDetailsSection as unknown as ISection,
  ];

  return {
    sections: extendSectionsType(sections),
  };
}

export function loadProgramPageConfig(
  program,
  serverConfig,
  landingPageConfig,
  { primaryColor, colors },
  options?: {
    isOfferMode?: boolean;
    offerData?: OfferDetailsData;
    offerMetadata?: { title?: string | null; description?: string | null };
  },
) {
  // Offer landing page mode
  if (options?.isOfferMode) {
    return loadOfferPageConfig(
      program,
      serverConfig,
      landingPageConfig,
      { primaryColor, colors },
      options.offerData,
      options.offerMetadata,
    );
  }

  // If serverConfig exists, use it (existing saved config)
  if (serverConfig?.sections) {
    return {
      sections: extendSectionsType(serverConfig.sections),
      ...serverConfig,
    };
  }

  // Existing OTP/Subscription logic (unchanged)
  const isSubscription = program.accessType === 'premium';
  const defaultConfig = isSubscription
    ? defaultSubscriptionPageConfig(program, {
        mainColor: primaryColor,
        landingPageConfig,
      })
    : defaultOTPPageConfig(program, {
        primaryColor,
        colors,
        landingPageConfig,
      });
  const res = {
    sections: extendSectionsType(defaultConfig.sections),
    ...serverConfig,
  };
  return res;
}

function parseProgramTestimonials(program) {
  if (!program?.testimonials) return [];

  return program.testimonials.map((testimonial) => {
    return {
      name: testimonial.name,
      text: testimonial.text,
      image: toImgSrcRaw(testimonial.media),
      video: toVideoSrcRaw(testimonial.media),
    };
  });
}
